import React, { useCallback, useMemo, useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Tooltip, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Button, Alert, Modal } from 'react-bootstrap';
import { useIsSmallScreen } from '../../hooks/useIsSmallScreen';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { useBuilding } from '../../Helpers/BuildingContext';
import { SectionLoader } from '../../Components/LoadingSpinner';
import { BaseType } from '../../types/Enums';
import { BaseComponentImpl } from '../../types/implementations';
import { CoordinatesModel } from '../../types/protocols';
import { getComponentIconPaths } from '../../Helpers/componentTypeIconUtils';
import { useBuildComponentTypes } from '../../contexts/BuildComponentTypesContext';
import ComponentDetailPane from './components/ComponentDetailPane';
import './GPSMapPage.css';

// Sweden center coordinates (fallback when no building coordinates)
const SWEDEN_CENTER: [number, number] = [62.0, 15.0];
const SWEDEN_ZOOM = 5;
const DEFAULT_ZOOM = 17;
// Tiles exist to level 19; past that Leaflet enlarges them, so close-set
// components (a meter and a valve on the same wall) can still be told apart.
const MAX_ZOOM = 21;
const MAX_TILE_ZOOM = 19;
// Esri's imagery stops at level 18 in parts of Sweden and answers deeper
// requests with grey "no data" tiles, so satellite enlarges level 18.
const MAX_SATELLITE_TILE_ZOOM = 18;

// Marker colors matching BlueprintWithMarkers
const PRIMARY_COLOR = '#2BABD6';
const PRIMARY_DARK_COLOR = '#001D60';

interface ComponentWithGPS {
    key: string;
    component: BaseComponentImpl;
    coordinates: CoordinatesModel;
}

// Component to fit map bounds to markers including building
const FitBoundsHandler: React.FC<{
    components: ComponentWithGPS[];
    buildingCoordinates?: CoordinatesModel;
}> = ({ components, buildingCoordinates }) => {
    const map = useMap();

    useEffect(() => {
        // Collect all points: components + building
        const points: [number, number][] = components.map(c =>
            [c.coordinates.latitude, c.coordinates.longitude]
        );

        if (buildingCoordinates) {
            points.push([buildingCoordinates.latitude, buildingCoordinates.longitude]);
        }

        if (points.length === 0) {
            // No points - center on Sweden
            map.setView(SWEDEN_CENTER, SWEDEN_ZOOM);
            return;
        }

        if (points.length === 1) {
            // Single point - center on it
            map.setView(points[0], DEFAULT_ZOOM);
            return;
        }

        // Multiple points - fit bounds
        const bounds = L.latLngBounds(points);
        map.fitBounds(bounds, { padding: [50, 50], maxZoom: DEFAULT_ZOOM });
    }, [map, components, buildingCoordinates]);

    return null;
};

// Create a custom circular marker icon with an image inside
const createCircularIcon = (
    iconUrl: string,
    backgroundColor: string,
    size: number = 36
): L.DivIcon => {
    const iconSize = size * 0.6;
    const padding = (size - iconSize) / 2;

    return L.divIcon({
        className: 'gps-map-custom-marker',
        html: `
            <div class="gps-marker-circle" style="
                width: ${size}px;
                height: ${size}px;
                background-color: ${backgroundColor};
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                <img
                    src="${iconUrl}"
                    style="
                        width: ${iconSize}px;
                        height: ${iconSize}px;
                        object-fit: contain;
                    "
                    onerror="this.src='/Images/placeholder.png'"
                />
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

// Create building marker icon (house icon)
const createBuildingIcon = (): L.DivIcon => {
    const size = 40;
    return L.divIcon({
        className: 'gps-map-building-marker',
        html: `
            <div class="gps-marker-circle" style="
                width: ${size}px;
                height: ${size}px;
                background-color: ${PRIMARY_DARK_COLOR};
                border-radius: 50%;
                border: 3px solid white;
                box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
                display: flex;
                align-items: center;
                justify-content: center;
            ">
                <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="white">
                    <path d="M10 20v-6h4v6h5v-8h3L12 3 2 12h3v8z"/>
                </svg>
            </div>
        `,
        iconSize: [size, size],
        iconAnchor: [size / 2, size / 2],
    });
};

const GPSMapPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const { currentBuilding, loadingBuilding, buildingError, fetchBuilding } = useBuilding();
    const { getBaseType } = useBuildComponentTypes();
    const [isSatellite, setIsSatellite] = useState(false);
    const [selectedComponentKey, setSelectedComponentKey] = useState<string | null>(null);
    const mapRef = useRef<L.Map | null>(null);

    useDocumentTitle(
        currentBuilding?.summary.name
            ? `${currentBuilding.summary.name} - GPS-karta`
            : 'GPS-karta'
    );

    // Collect all components with GPS coordinates
    const componentsWithGPS: ComponentWithGPS[] = useMemo(() => {
        if (!currentBuilding?.model.structure?.components) {
            return [];
        }

        const result: ComponentWithGPS[] = [];
        const components = currentBuilding.model.structure.components;

        for (const [key, component] of Object.entries(components)) {
            // Skip archived components
            if (component.archivedAt) continue;

            // Check if component has GPS coordinates
            const gpsData = (component as any).gpsCoordinates;
            if (!gpsData?.value) continue;

            const coords = gpsData.value as CoordinatesModel;
            if (typeof coords.latitude !== 'number' || typeof coords.longitude !== 'number') {
                continue;
            }

            result.push({
                key,
                component: new BaseComponentImpl({ ...component, key }),
                coordinates: coords
            });
        }

        return result;
    }, [currentBuilding]);

    // Get icon paths with fallback for a component
    const getIconPath = (component: BaseComponentImpl): string => {
        const specificTypeValue = (component as any).specificType?.value as string | undefined;
        const iconPaths = getComponentIconPaths(specificTypeValue, component.type, getBaseType);
        return iconPaths.primary;
    };

    // Cache icons by key (component key -> icon)
    const iconCache = useMemo(() => {
        const cache = new Map<string, L.DivIcon>();
        componentsWithGPS.forEach(({ key, component }) => {
            const iconPath = getIconPath(component);
            cache.set(key, createCircularIcon(iconPath, PRIMARY_COLOR));
        });
        return cache;
    }, [componentsWithGPS, getBaseType]);

    const buildingIcon = useMemo(() => createBuildingIcon(), []);

    const handleComponentClick = (componentKey: string) => {
        // Toggle selection: if already selected, deselect
        setSelectedComponentKey(prev => prev === componentKey ? null : componentKey);
    };

    const toggleTileLayer = () => setIsSatellite(prev => !prev);

    // Get selected component for detail pane
    const selectedComponent = useMemo(() => {
        if (!selectedComponentKey) return null;
        const found = componentsWithGPS.find(c => c.key === selectedComponentKey);
        return found?.component ?? null;
    }, [selectedComponentKey, componentsWithGPS]);

    // A deleted component also changes the components it detached, so re-read the building
    const handleComponentDeleted = useCallback(() => {
        setSelectedComponentKey(null);
        if (buildingKey) fetchBuilding(buildingKey).then();
    }, [buildingKey, fetchBuilding]);

    // Phones give the whole screen to the map; a tapped marker opens its
    // component in a modal instead of the sidebar.
    const isSmallScreen = useIsSmallScreen();

    if (loadingBuilding) {
        return (
            <div className="gps-map-page">
                <SectionLoader />
            </div>
        );
    }

    if (buildingError || !currentBuilding) {
        return (
            <div className="gps-map-page">
                <Alert variant="danger">
                    {buildingError || 'Byggnaden kunde inte laddas.'}
                </Alert>
            </div>
        );
    }

    const buildingCoordinates = currentBuilding.summary.coordinates;
    const initialCenter: [number, number] = buildingCoordinates
        ? [buildingCoordinates.latitude, buildingCoordinates.longitude]
        : SWEDEN_CENTER;
    const initialZoom = buildingCoordinates ? DEFAULT_ZOOM : SWEDEN_ZOOM;

    return (
        <div className="gps-map-page">
            <div style={{ flexShrink: 0 }}>
                <BreadcrumbComponent
                    items={[
                        { text: currentBuilding.summary.name || 'Byggnad', link: `/building/${buildingKey}` },
                        { text: 'GPS-karta' }
                    ]}
                />
            </div>

            <div className="gps-map-layout">
                {/* Main map area */}
                <div className="gps-map-main">
                    <div className="gps-map-container">
                        <MapContainer
                            center={initialCenter}
                            zoom={initialZoom}
                            style={{ height: '100%', width: '100%' }}
                            scrollWheelZoom={true}
                            maxZoom={MAX_ZOOM}
                            ref={mapRef}
                        >
                            {/* Keys make React build a new layer on each switch: react-leaflet
                                only updates the url of an existing layer, not its zoom options. */}
                            {!isSatellite ? (
                                <TileLayer
                                    key="map"
                                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                                    maxZoom={MAX_ZOOM}
                                    maxNativeZoom={MAX_TILE_ZOOM}
                                />
                            ) : (
                                <TileLayer
                                    key="satellite"
                                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                                    attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                                    maxZoom={MAX_ZOOM}
                                    maxNativeZoom={MAX_SATELLITE_TILE_ZOOM}
                                />
                            )}

                            <FitBoundsHandler
                                components={componentsWithGPS}
                                buildingCoordinates={buildingCoordinates}
                            />

                            {/* Building marker */}
                            {buildingCoordinates && (
                                <Marker
                                    position={[buildingCoordinates.latitude, buildingCoordinates.longitude]}
                                    icon={buildingIcon}
                                    zIndexOffset={-100}
                                >
                                    <Tooltip
                                        direction="top"
                                        offset={[0, -20]}
                                        className="gps-map-tooltip"
                                    >
                                        {currentBuilding.summary.name || 'Byggnad'}
                                    </Tooltip>
                                </Marker>
                            )}

                            {/* Component markers */}
                            {componentsWithGPS.map(({ key, component, coordinates }) => {
                                const isSelected = selectedComponentKey === key;
                                const icon = iconCache.get(key);
                                if (!icon) return null;

                                return (
                                    <Marker
                                        key={key}
                                        position={[coordinates.latitude, coordinates.longitude]}
                                        icon={icon}
                                        zIndexOffset={isSelected ? 1000 : 0}
                                        eventHandlers={{
                                            click: () => handleComponentClick(key)
                                        }}
                                    >
                                        <Tooltip
                                            direction="top"
                                            offset={[0, -18]}
                                            className="gps-map-tooltip"
                                        >
                                            {component.displayName(currentBuilding)}
                                        </Tooltip>
                                    </Marker>
                                );
                            })}
                        </MapContainer>

                        {/* Empty state overlay */}
                        {componentsWithGPS.length === 0 && (
                            <div className="gps-map-empty-state">
                                <div className="gps-map-empty-state-content">
                                    Inga komponenter har GPS-position
                                </div>
                            </div>
                        )}

                        {/* Layer toggle button */}
                        <Button
                            className="gps-map-layer-toggle"
                            variant="light"
                            onClick={toggleTileLayer}
                        >
                            <img
                                style={{ width: '30px', padding: '2px' }}
                                alt="Byt kartlager"
                                src="/Images/layers.png"
                            />
                        </Button>
                    </div>
                </div>

                {/* Right sidebar with component detail pane */}
                {!isSmallScreen && (
                    <div className="gps-map-sidebar">
                        <ComponentDetailPane
                            selectedComponent={selectedComponent}
                            building={currentBuilding}
                            buildingKey={buildingKey || ''}
                            hasComponents={componentsWithGPS.length > 0}
                            onDeleted={handleComponentDeleted}
                        />
                    </div>
                )}
            </div>

            <Modal
                show={isSmallScreen && !!selectedComponent}
                onHide={() => setSelectedComponentKey(null)}
                scrollable
            >
                <Modal.Header closeButton>
                    <Modal.Title>Komponent</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <ComponentDetailPane
                        selectedComponent={selectedComponent}
                        building={currentBuilding}
                        buildingKey={buildingKey || ''}
                        hasComponents
                        inModal
                        onDeleted={handleComponentDeleted}
                    />
                </Modal.Body>
            </Modal>
        </div>
    );
};

export default GPSMapPage;
